/**
 * Step 2: 故事蓝图
 * - 目标年龄（卡片式单选，RAZ分级）
 * - 绘本页数（分段控制器）
 * - 故事主题（网格卡片，单选）
 */
import { motion } from 'framer-motion';
import { useCreationStore } from '../../../store/useCreationStore';
import { Clock, FileText, Volume2, Lock, PenLine, ChevronDown } from 'lucide-react';
import TagInput from '../../../components/TagInput';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 年龄对应的默认语速
const DEFAULT_SPEED_BY_AGE: Record<string, number> = {
  '3-4': 0.85,
  '5-6': 0.9,
  '7-8': 0.95,
};

// 语速描述
const getSpeedLabel = (speed: number) => {
  if (speed <= 0.75) return '较慢';
  if (speed <= 0.85) return '慢速';
  if (speed <= 0.95) return '适中';
  if (speed <= 1.1) return '快速';
  return '较快';
};

// 语速建议
const getSpeedTip = (speed: number) => {
  if (speed < 0.9) return '适合低龄儿童，有更多时间理解内容';
  if (speed < 1.0) return '适合大多数孩子，节奏舒适';
  return '适合阅读能力较强的大孩子';
};

const ageOptions = [
  {
    value: '3-4',
    label: '3-4岁',
    level: 'AA级',
    desc: '简单词汇，重复句式',
    color: 'from-green-400 to-emerald-500',
  },
  {
    value: '5-6',
    label: '5-6岁',
    level: 'A-B级',
    desc: '基础对话，描述性词汇',
    color: 'from-blue-400 to-indigo-500',
  },
  {
    value: '7-8',
    label: '7-8岁',
    level: 'C-D级',
    desc: '完整叙述，复合句',
    color: 'from-violet-400 to-purple-500',
  },
];



const themeOptions = [
  { id: 'friendship', label: '友谊与冒险', desc: '关于朋友之间的温暖故事', emoji: '🤝' },
  { id: 'bravery', label: '勇气与成长', desc: '克服困难，变得勇敢', emoji: '🦁' },
  { id: 'family', label: '家庭与爱', desc: '温馨的家庭时光', emoji: '🏠' },
  { id: 'nature', label: '自然与探索', desc: '探索大自然的奥秘', emoji: '🌲' },
  { id: 'dream', label: '梦想与未来', desc: '追逐梦想的故事', emoji: '⭐' },
  { id: 'magic', label: '魔法与奇幻', desc: '神奇的魔法世界', emoji: '🔮' },
];

export default function Step2Blueprint() {
  const { story, setStory, setVisual, flow } = useCreationStore();
  const [searchParams] = useSearchParams();
  const hasLoggedRef = useRef(false);
  const [isPageLocked, setIsPageLocked] = useState(false);
  const isDIY = flow === 'diy';

  const [showPageDropdown, setShowPageDropdown] = useState(false);
  const pageInputRef = useRef<HTMLInputElement>(null);

  // 点击外部关闭页数下拉
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const container = dropdownRef.current;
      if (container && !container.contains(e.target as Node)) {
        setShowPageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPageTime = (pages: number) => {
    const min = Math.max(1, Math.round(pages * 0.4));
    const max = Math.max(2, Math.round(pages * 0.7));
    return `约需 ${min}-${max} 分钟`;
  };

  // 控制台输出当前页面数据参数
  useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('[Step2Blueprint] Component mounted, initial story data:', {
        age: story.age,
        pages: story.pages,
        theme: story.theme,
        speed: story.speed,
        outline: story.outline,
      });
    }
  }, []);

  useEffect(() => {
    console.log('[Step2Blueprint] Story data changed:', {
      age: story.age,
      pages: story.pages,
      theme: story.theme,
      speed: story.speed,
      outline: story.outline,
    });
  }, [story.age, story.pages, story.theme, story.speed, story.outline]);

  // 自动保存到数据库（debounce 2秒）
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    if (!storyId) return;

    const timer = setTimeout(async () => {
      const token = localStorage.getItem('token');
      const store = useCreationStore.getState();
      const data = store.getCreationData();
      const currentStory = store.story;

      // 如果数据完全为空，跳过自动保存
      if (!currentStory.age && !currentStory.theme) {
        console.log('[Step2Blueprint] Skip auto-save: data empty');
        return;
      }

      try {
        const payload = {
          storyId,
          photoUrl: data.character.photoUrl || '',
          appearance: data.character.appearance || '',
          characterName: data.character.name || '',
          characterType: data.character.characterType || 'human',
          gender: data.character.gender,
          interests: data.character.traits || [],
          targetAge: currentStory.age || '5-6',
          artStyle: data.visual.style || '',
          theme: currentStory.theme || '',
          tags: currentStory.tags || [],
          pageCount: currentStory.pages || 6,
          outline: currentStory.outline || '',
          speed: currentStory.speed,
          voiceId: data.visual.voice || 'xiaoyan',
          flow,
        };
        console.log('[Step2Blueprint] Auto-saving draft:', payload);

        const res = await fetch(`${API_BASE_URL}/stories/setup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (result.success) {
          console.log('[Step2Blueprint] Auto-saved to DB, storyId:', result.data?.storyId);
        } else {
          console.error('[Step2Blueprint] Auto-save failed:', result.error);
        }
      } catch (err) {
        console.error('[Step2Blueprint] Auto-save request failed:', err);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [story.age, story.pages, story.theme, story.outline, story.speed, searchParams]);

  // 当年龄改变时，自动设置默认语速
  useEffect(() => {
    if (story.age && !story.speed) {
      setStory({ speed: DEFAULT_SPEED_BY_AGE[story.age] || 0.9 });
    }
  }, [story.age]);

  // 检测绘本是否已生成文案，锁定页数
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    if (!storyId) return;

    const checkLock = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/stories/${storyId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await res.json();
        if (result.success && result.data) {
          const hasTextContent = !!result.data.textContent;
          // DIY 模式下不锁定页数，允许灵活修改
          setIsPageLocked(!isDIY && hasTextContent);
          console.log('[Step2Blueprint] Page lock status:', { isDIY, hasTextContent, locked: !isDIY && hasTextContent });
        }
      } catch (err) {
        console.error('[Step2Blueprint] Failed to check page lock:', err);
      }
    };

    checkLock();
  }, [searchParams, isDIY]);

  // 兜底恢复：如果组件 mount 时发现数据为空但 URL 中有 storyId，从数据库恢复
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    const isEmpty = !story.age && !story.theme && (story.pages === 6 || story.pages === 12);
    if (isEmpty && storyId) {
      console.log('[Step2Blueprint] Data empty but storyId exists, auto-recovering...');
      recoverFromDB(storyId);
    }
  }, []);

  const recoverFromDB = async (storyId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success && result.data) {
        const dbStory = result.data;
        const config = typeof dbStory.config === 'string' ? JSON.parse(dbStory.config) : (dbStory.config || {});
        // 优先从 audioPreference 恢复 speed（实际生效的值），其次从 config.speed
        const audioPref = dbStory.audioPreference as any;
        const recoveredSpeed = audioPref?.speed !== undefined ? audioPref.speed : config.speed;
        setStory({
          age: dbStory.targetAge || '5-6',
          pages: dbStory.pageCount || 6,
          theme: dbStory.theme || '',
          tags: dbStory.tags || (dbStory.theme ? [dbStory.theme] : []),
          outline: config.outline || '',
          speed: recoveredSpeed,
        });
        // 同步恢复音色到 visual store
        const recoveredVoice = audioPref?.voiceId || config.voiceId || 'xiaoyan';
        setVisual({ voice: recoveredVoice });
        // 恢复时也检查页数锁定状态（DIY 模式下不锁定）
        if (dbStory.textContent) {
          setIsPageLocked(!isDIY);
        }
        console.log('[Step2Blueprint] Auto-recovered from DB');
      }
    } catch (err) {
      console.error('[Step2Blueprint] Auto-recover failed:', err);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* 目标年龄（已在 Step1 角色设定中选择，此处仅同步显示） */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          目标年龄
          <span className="text-slate-400 font-normal ml-2 text-xs">已在角色设定中选择</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {ageOptions.map((age) => (
            <div
              key={age.value}
              className={`
                relative rounded-2xl border-2 transition-all duration-300 text-left
                ${story.age === age.value
                  ? 'border-amber-400 bg-white shadow-lg shadow-amber-500/10'
                  : 'border-slate-100 bg-slate-50/30 opacity-50'
                }
              `}
              style={{ padding: '12px' }}
            >
              {/* 年龄段标签 */}
              <div className={`inline-block rounded-md text-xs font-bold text-white bg-gradient-to-r ${age.color}`} style={{ padding: '8px', marginBottom: '8px' }}>
                {age.level}
              </div>
              
              <div className="text-lg font-bold text-slate-800" style={{ marginBottom: '8px' }}>{age.label}</div>
              <p className="text-xs text-slate-500">{age.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 绘本页数 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          绘本页数 <span className="text-red-500">*</span>
        </label>
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                ref={pageInputRef}
                type="number"
                min={1}
                value={story.pages ?? 6}
                disabled={isPageLocked}
                onFocus={() => !isPageLocked && setShowPageDropdown(true)}
                onChange={(e) => {
                  const raw = e.target.value;
                  const val = parseInt(raw);
                  const newPages = isNaN(val) || val < 1 ? 1 : val;
                  console.log('[PageInput] onChange:', { raw, val, newPages, currentPages: story.pages });
                  setStory({ pages: newPages });
                }}
                className={`
                  w-full bg-white border rounded-xl px-4 py-2.5 pr-10 text-base text-center
                  focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500
                  transition-all
                  ${isPageLocked
                    ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
                    : 'border-slate-200'
                  }
                `}
              />
              <span className="absolute right-10 top-1/2 -translate-y-1/2 text-slate-400 text-sm">页</span>
              <button
                type="button"
                disabled={isPageLocked}
                onClick={() => !isPageLocked && setShowPageDropdown(!showPageDropdown)}
                className={`
                  absolute right-3 top-1/2 -translate-y-1/2
                  ${isPageLocked ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-slate-600'}
                `}
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${showPageDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* 下拉选项面板 */}
          {showPageDropdown && !isPageLocked && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-2">
              <div className="grid grid-cols-4 gap-1">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      console.log('[PageDropdown] selected:', n, 'current:', story.pages);
                      setStory({ pages: n });
                      setShowPageDropdown(false);
                    }}
                    className={`
                      py-2 rounded-lg text-sm transition-colors
                      ${story.pages === n
                        ? 'bg-indigo-100 text-indigo-600 font-medium'
                        : 'hover:bg-slate-50 text-slate-700'
                      }
                    `}
                  >
                    {n}页
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 预计时间 或 锁定提示 */}
        <motion.div
          key={story.pages}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-sm" style={{ marginTop: '8px' }}
        >
          {isPageLocked ? (
            <>
              <Lock className="w-4 h-4 text-amber-500" />
              <span className="text-amber-600">文案已生成，页数不可修改。如需修改请重新开始创作</span>
            </>
          ) : (
            <>
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-slate-500">{getPageTime(story.pages)}</span>
            </>
          )}
        </motion.div>
      </div>

      {/* 故事主题（AI 模式显示，DIY 隐藏） */}
      {!isDIY && (
        <div style={{ padding: '8px 0' }}>
          <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
            故事主题 <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {themeOptions.map((theme) => (
              <motion.button
                key={theme.id}
                onClick={() => setStory({ theme: theme.id, tags: [...(story.tags || []).filter((t: string) => !themeOptions.map((o: any) => o.id).includes(t)), theme.id] })}
                whileHover={{ y: -2, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`
                  relative rounded-2xl border-2 transition-all duration-300 text-left
                  ${story.theme === theme.id
                    ? 'border-amber-400 bg-amber-50/50 shadow-lg shadow-amber-500/10'
                    : 'border-slate-200 bg-white hover:border-indigo-200 hover:shadow-md'
                  }
                `}
              >
                {/* 选中标记 */}
                {story.theme === theme.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-2 right-2 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                )}

                <div className="flex items-start gap-2">
                  <span className="text-2xl">{theme.emoji}</span>
                  <div>
                    <div className="font-semibold text-slate-800" style={{ marginBottom: '8px' }}>{theme.label}</div>
                    <p className="text-xs text-slate-500">{theme.desc}</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* 标签输入（DIY 和 AI 模式都显示） */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          故事标签
          <span className="text-slate-400 font-normal ml-2 text-xs">选填，帮助分类和发现</span>
        </label>
        <TagInput
          tags={story.tags || []}
          onChange={(newTags) => setStory({ tags: newTags })}
          suggestions={themeOptions.map(t => t.id)}
        />
      </div>

      {/* DIY 模式：提示信息 */}
      {isDIY && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" style={{ margin: '8px 0' }}>
          <PenLine className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">✏️ 亲子自制模式</p>
            <p className="text-xs text-amber-600 mt-1">你将在下一步的编辑器中逐页创作故事。故事主题和大纲由你自由发挥，无需在这里填写。</p>
          </div>
        </div>
      )}

      {/* 语速调节 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          <span className="flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-indigo-500" />
            朗读语速
          </span>
        </label>
        
        <div className="bg-slate-50 rounded-xl border border-slate-200" style={{ padding: '8px' }}>
          {/* 语速值显示 */}
          <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-indigo-600">
                {((story.speed || 0.9) * 100).toFixed(0)}%
              </span>
              <span className="text-sm text-slate-500">
                ({getSpeedLabel(story.speed || 0.9)})
              </span>
            </div>
            <span className="text-xs text-slate-400 bg-white rounded-md" style={{ padding: '8px' }}>
              {getSpeedTip(story.speed || 0.9)}
            </span>
          </div>
          
          {/* 滑块 */}
          <div className="relative">
            <input
              type="range"
              min={0.7}
              max={1.3}
              step={0.05}
              value={story.speed || 0.9}
              onChange={(e) => setStory({ speed: parseFloat(e.target.value) })}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-500 hover:accent-indigo-600 transition-all"
              style={{
                background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${((story.speed || 0.9) - 0.7) / (1.3 - 0.7) * 100}%, #e2e8f0 ${((story.speed || 0.9) - 0.7) / (1.3 - 0.7) * 100}%, #e2e8f0 100%)`
              }}
            />
            {/* 刻度标记 */}
            <div className="flex justify-between text-xs text-slate-400" style={{ marginTop: '8px' }}>
              <span>慢</span>
              <span className="text-indigo-500 font-medium">默认</span>
              <span>快</span>
            </div>
          </div>
          
          {/* 年龄提示 */}
          <div className="text-xs text-slate-400 flex items-center gap-2" style={{ marginTop: '8px' }}>
            <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
            {story.age ? `根据 ${story.age}岁 推荐的默认语速` : '请先选择目标年龄'}
          </div>
        </div>
      </div>

      {/* 故事梗概（AI 模式显示，DIY 隐藏） */}
      {!isDIY && (
        <div style={{ padding: '8px 0' }}>
          <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-500" />
              故事梗概 <span className="text-red-500">*</span>
            </span>
          </label>
          <textarea
            value={story.outline}
            onChange={(e) => setStory({ outline: e.target.value })}
            placeholder="比如：小明和他的小狗在森林里寻找彩虹瀑布..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none text-base"
            style={{ minHeight: '96px', padding: '8px' }}
          />
          <p className="text-xs text-slate-400" style={{ marginTop: '8px' }}>
            提供一个大致的故事方向，AI 会在此基础上创作
          </p>
        </div>
      )}
    </div>
  );
}
